import { useEffect } from "react";
import { createPortal } from "react-dom";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { getFullName } from "../../reports-settings/report-type/helpers/form.handler";

const DisableScroll: React.FC = () => {
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);
  return null;
};

// Custom Modal Component
const ShowMoreModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  items: any[];
}> = ({ isOpen, onClose, items }) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!isOpen) return null;

  // Prevent event propagation for modal content
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Handle backdrop click (only close when clicking backdrop, not content)
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if the clicked element is the backdrop itself
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <>
      {/* Disable scroll component */}
      <DisableScroll />

      {/* Modal backdrop */}
      <div
        className="modal-backdrop"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          padding: "20px",
        }}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title">
        {/* Modal content */}
        <div
          className="modal-content"
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            width: "100%",
            maxWidth: "600px",
            maxHeight: "80vh",
            overflowY: "auto",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            position: "relative",
            outline: "none",
          }}
          onClick={handleContentClick}
          tabIndex={-1}>
          {/* Modal Header */}
          <div
            className="modal-header"
            style={{
              padding: "20px",
              borderBottom: "1px solid #e9ecef",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <h4
              id="modal-title"
              className="modal-title"
              style={{ margin: 0, fontSize: "1.25rem", fontWeight: "600" }}>
              Visible To
            </h4>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: "4px",
                color: "#6c757d",
                lineHeight: 1,
                transition: "background-color 0.2s ease",
              }}
              aria-label="Close modal"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}>
              ×
            </button>
          </div>

          {/* Modal Body */}
          <div
            className="modal-body"
            style={{
              padding: "20px",
              overflowY: "auto",
              maxHeight: "calc(80vh - 160px)",
            }}>
            <div className="row g-3">
              {items.map((item) => (
                <div key={item?.id} className="col-md-6">
                  <div
                    className="d-flex align-items-center p-3 bg-light rounded-3"
                    style={{
                      transition: "background-color 0.2s ease",
                      cursor: "default",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#e9ecef";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }}>
                    <div className="avatar avatar-xl avatar-rounded object-cover flex-shrink-0 me-3">
                      <ImageWithBasePath
                        src={
                          !item?.profile_pic || item?.profile_pic === "null"
                            ? "assets/img/users/user.png"
                            : item?.profile_pic
                        }
                        alt="Profile"
                      />
                    </div>

                    <div className="flex-grow-1">
                      <div style={{ fontWeight: "500", fontSize: "14px" }}>
                        {getFullName(
                          item?.salutation,
                          item?.fname,
                          item?.lname
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length === 0 && (
              <div className="text-center py-4">
                <i className="ti ti-users text-muted"></i>
                <p className="text-muted mt-2 mb-0">No users assigned</p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div
            className="modal-footer"
            style={{
              padding: "20px",
              borderTop: "1px solid #e9ecef",
              display: "flex",
              justifyContent: "flex-end",
            }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onClose}
              style={{
                minWidth: "80px",
              }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ShowMoreModal;
